'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const quickLinks = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'Instant Quote', href: '/instant-quote' },
    { label: 'About Us', href: '/about' },
    { label: 'Resources', href: '/resources' }
  ];

  const services = [
    { label: 'Residential Moving', href: '/services#residential' },
    { label: 'Commercial Relocation', href: '/services#commercial' },
    { label: 'Packing Services', href: '/services#packing' },
    { label: 'Assembly & Installation', href: '/services#assembly' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: 'AtSymbolIcon', href: 'https://instagram.com/almanara_3985' },
    { name: 'WhatsApp', icon: 'ChatBubbleLeftRightIcon', href: 'https://wa.me/96569911181' },
    { name: 'Phone', icon: 'PhoneIcon', href: 'tel:69911181' }
  ];

  if (!isHydrated) {
    return (
      <footer className={`bg-primary text-white py-12 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white/10 rounded w-1/4"></div>
            <div className="h-4 bg-white/10 rounded w-1/2"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`bg-primary text-white ${className}`}>
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link href="/homepage" className="flex items-center space-x-3 mb-4">
              <div className="relative w-12 h-12">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <circle cx="24" cy="24" r="22" fill="white" opacity="0.1" />
                  <path d="M24 8L24 28M24 28L16 20M24 28L32 20" stroke="var(--color-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="24" cy="32" r="3" fill="var(--color-accent)" />
                  <path d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-headline font-bold leading-tight">Almanara</span>
                <span className="text-sm font-body text-white/80 leading-tight">Moving</span>
              </div>
            </Link>
            <p className="text-white/80 font-body leading-relaxed mb-4">
              Your trusted moving partner in Kuwait, guiding you through life's transitions with care and professionalism.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-base"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} variant="solid" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={`quicklink-${link.href}-${index}`}>
                  <Link
                    href={link.href}
                    className="text-white/80 hover:text-white transition-colors duration-base font-body flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} variant="solid" className="group-hover:translate-x-1 transition-transform duration-base" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Our Services</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={`service-${service.href}-${index}`}>
                  <Link
                    href={service.href}
                    className="text-white/80 hover:text-white transition-colors duration-base font-body flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} variant="solid" className="group-hover:translate-x-1 transition-transform duration-base" />
                    <span>{service.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-headline font-bold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:69911181" className="flex items-start space-x-3 text-white/80 hover:text-white transition-colors duration-base group">
                  <Icon name="PhoneIcon" size={20} variant="solid" className="flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-base" />
                  <div>
                    <p className="font-body font-semibold text-white">24/7 Hotline</p>
                    <p className="font-accent text-lg">6991 1181</p>
                  </div>
                </a>
              </li>
              <li>
                <a href="mailto:info@almanaramoving.com" className="flex items-start space-x-3 text-white/80 hover:text-white transition-colors duration-base group">
                  <Icon name="EnvelopeIcon" size={20} variant="solid" className="flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-base" />
                  <div>
                    <p className="font-body font-semibold text-white">Email</p>
                    <p className="font-body text-sm">info@almanaramoving.com</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start space-x-3 text-white/80">
                  <Icon name="MapPinIcon" size={20} variant="solid" className="flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-body font-semibold text-white">Office</p>
                    <p className="font-body text-sm">Farwaniya District, Kuwait</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-white/60 text-sm font-body text-center md:text-left">
              &copy; {currentYear} Almanara Moving. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link href="/privacy" className="text-white/60 hover:text-white transition-colors duration-base font-body">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-white/60 hover:text-white transition-colors duration-base font-body">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;